import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Button } from '@shadcn/components/ui/button';
import { Card, CardContent as CardBody } from '@shadcn/components/ui/card';
import { Row } from '@shadcn/components/ui/row';
import { Col } from '@shadcn/components/ui/col';

import useStockStore from '@zustand/useStockStore.js';
import { Input } from '@shadcn/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shadcn/components/ui/select';
import ProductVariantSelector from '@pages/JIFI/helpers/ProductVariantSelector';
import superagent from 'superagent';
import { toast } from 'react-toastify';
import { backendUrl } from '../../../../helpers/consts.js';

const StockInput = ({ productData, handleSubmit, validate, toggleModal, label, excludeContainerLineIds }) => {
  const { stock, addStock, updateStock, removeStock } = useStockStore();
  const [factoryCache, setFactoryCache] = useState({});
  const [loadingFactories, setLoadingFactories] = useState({});
  const fetchedSkus = useRef(new Set());

  const products = productData || [];

  const handleAddRow = () => {
    const firstProduct = products[0];
    const defaultVariant = firstProduct?.variants?.[0];

    if (!firstProduct || !defaultVariant) {
      toast.warning('Default product or variant is missing');
      return;
    }

    addStock({
      product: firstProduct._id,
      variant: defaultVariant,
      sku: defaultVariant.sku,
      quantity: 0,
      costPerItem: 0,
      factoryId: '',
      factoryOptions: [],
    });
  };

  const fetchFactories = useCallback(async (index, sku) => {
    if (!sku) {
      updateStock(index, 'factoryOptions', []);
      updateStock(index, 'factoryId', '');
      return;
    }

    if (factoryCache[sku]) {
      const options = factoryCache[sku].map(item => ({
        label: `${item.factory?.code || ''} - ${item.factory?.name || ''}`.trim(),
        value: item.factory?.id || '',
      }));

      updateStock(index, 'factoryOptions', options);
      fetchedSkus.current.add(sku);
      return;
    }

    if (fetchedSkus.current.has(sku)) {
      return;
    }

    fetchedSkus.current.add(sku);
    setLoadingFactories(prev => ({ ...prev, [index]: true }));

    try {
      const queryParams = { sku };

      if (excludeContainerLineIds && excludeContainerLineIds.length > 0) {
        queryParams.excludeContainerLineIds = excludeContainerLineIds.join(',');
      }

      const response = await superagent
        .get(`${backendUrl}/purchase-order/sku-factory-allocations`)
        .query(queryParams)
        .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`)
        .set('accept', 'json');

      const allocations = Array.isArray(response.body) ? response.body : [];
      const options = allocations.map(item => ({
        label: `${item.factory?.code || ''} - ${item.factory?.name || ''}`.trim(),
        value: item.factory?.id || '',
      }));

      setFactoryCache(prev => ({ ...prev, [sku]: allocations }));
      updateStock(index, 'factoryOptions', options);
    } catch (error) {
      toast.error('Unable to load factories for this SKU');
      updateStock(index, 'factoryOptions', []);
      updateStock(index, 'factoryId', '');
      fetchedSkus.current.delete(sku);
    } finally {
      setLoadingFactories(prev => ({ ...prev, [index]: false }));
    }
  }, [factoryCache, updateStock, excludeContainerLineIds]);

  const handleVariantChange = useCallback((index, variant) => {
    updateStock(index, 'variant', variant);
    updateStock(index, 'sku', variant?.sku || '');
    fetchFactories(index, variant?.sku);
  }, [updateStock, fetchFactories]);

  useEffect(() => {
    stock.forEach((line, index) => {
      const sku = line?.variant?.sku || line?.sku;

      if (!sku || line.factoryOptions?.length || loadingFactories[index]) {
        return;
      }

      fetchFactories(index, sku);
    });

  }, [stock.length]);

  const totals = useMemo(() => {
    return stock.reduce((acc, line) => ({
      quantity: acc.quantity + parseInt(line.quantity || 0, 10),
      cost: acc.cost + Number(line.quantity || 0) * Number(line.costPerItem || 0),
    }), { quantity: 0, cost: 0 });
  }, [stock]);

  return (
    <Card className="mb-1">
      <CardBody>
        <Row className="pt-4">
          <Col className="sm:w-4/12">Variant</Col>
          <Col className="sm:w-2/12">Quantity</Col>
          <Col className="sm:w-2/12">FOB</Col>
          <Col className="sm:w-3/12">Factory</Col>
        </Row>
        <br />
        {productData && stock?.map((line, index) => (
          <Row key={index} style={{ padding: '12px 0' }} className="gap-2">
            <Col className="sm:w-4/12">
              <ProductVariantSelector
                hidePersonalisationOptions
                products={products}
                setSelectedVariant={(val) => handleVariantChange(index, val)}
                selectedVariant={line.variant}
              />
            </Col>
            <Col className="sm:w-2/12">
              <Input
                style={{ width: '90%', height: '100%' }}
                type="number"
                value={line.quantity}
                onChange={(e) => updateStock(index, 'quantity', e.target.value)}
              />
            </Col>
            <Col className="sm:w-2/12">
              <Input
                style={{ width: '90%', height: '100%' }}
                type="number"
                value={line.costPerItem}
                onChange={(e) => updateStock(index, 'costPerItem', e.target.value)}
              />
            </Col>
            <Col className="sm:w-3/12">
              <Select
                disabled={loadingFactories[index]}
                value={line.factoryId || ''}
                onValueChange={(value) => updateStock(index, 'factoryId', value === '__clear__' ? '' : value)}
              >
                <SelectTrigger style={{ width: '100%', height: '100%' }}>
                  <SelectValue placeholder="Select factory" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__clear__">Clear selection</SelectItem>
                  {line.factoryOptions?.map((factory) => (
                    <SelectItem key={factory.value} value={factory.value}>
                      {factory.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Col>
            <Col className="sm:w-1/12">
              <Button onClick={() => removeStock(index)} variant="destructive">
                X
              </Button>
            </Col>
          </Row>
        ))}
        {productData && totals.quantity !== 0 && (
          <Row className="gap-2" key="total" style={{ padding: '12px 0' }}>
            <Col className="sm:w-4/12">Total</Col>
            <Col className="sm:w-2/12 px-3">{totals.quantity || 0}</Col>
            <Col className="sm:w-2/12 px-3">{totals.cost || 0}</Col>
            <Col className="sm:w-1/12"></Col>
          </Row>
        )}
        {productData?.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginTop: 12 }}>
            <Button onClick={handleAddRow} variant="primary">
              Add
            </Button>
            <span />
          </div>
        )}
      </CardBody>
      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginTop: 12 }}>
        <span />
        {productData && stock.length > 0 && (
          <Button
            onClick={handleSubmit(async (data) => {
              const result = await validate(data);
              if (result) {
                toggleModal();
              }
            })}
            variant="primary"
            className="btn-lg btn-rounded mb-4 mr-4"
          >
            {label}
          </Button>
        )}
      </div>
    </Card>
  );
};


export default StockInput;
